import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash.debounce';
import FontAwesome from 'react-fontawesome';
import BranchResult from './BranchResult';
import EmployeeResult from './EmployeeResult.jsx';
import { Screens, FlowSteps, defaultSchema } from '../../state';
import {FormContainer, FormInput, NeuroMessage, useNeuroMessages} from '../../../components';
import { formatPhoneNumberField, BranchShape, EmployeeShape, LoginFlowScreenPropTypes } from '../../../utils';

const EditForm = ({
  branches: branchSearchResults,
  changeScreen,
  employeeConflicts,
  loginFlowChanged,
  onEmployeeSelected,
  formSchema = {},
  loadBranches,
  saveEmployeeProfile,
  searchEmployeeConflicts,
  selectedEmployee,
  processing,
  onCancel,
}) => {
  const messageContext = useNeuroMessages();

  const cssBEM = 'edit-form';
  const [showErrors, setShowErrors] = useState(false);
  const [formValues, setFormValues] = useState({});
  const [assignedBranches, setAssignedBranches] = useState(
    selectedEmployee.branches || []
  );
  const [searchValue, setSearchValue] = useState('');
  const [initiatedSearch, setInitiatedSearch] = useState(false);
  useEffect(() => loginFlowChanged(null, FlowSteps.BRANCH_SELECTED), []);

  const finalSchema = {
    fields: formSchema.fields || defaultSchema.fields,
    required: formSchema.required || defaultSchema.required,
  };

  const searchForBranches = useRef(
    debounce(
      (searchValue) => {
        loadBranches(searchValue);
        setInitiatedSearch(true);
      },
      1000,
      { maxWait: 5000 }
    )
  );

  const searchForEmployeeConflicts = useRef(
    debounce((form) => {
      searchEmployeeConflicts(form);
    }, 1000)
  );

  const searchValueChanged = (event) => {
    setInitiatedSearch(false);
    const searchValue = event.target.value || '';
    setSearchValue(searchValue);
    if (searchValue) {
      searchForBranches.current(searchValue);
    }
  };

  const formChanged = (form) => {
    if (form !== formValues.email || form !== formValues.employeeId) {
      searchForEmployeeConflicts.current(form);
    }
    setFormValues(form);
  };

  const formSubmitted = (form) => {
    console.log('Form Submitted', form);
    if (!assignedBranches.length || employeeConflicts.length) {
      setShowErrors(true);
    } else {
      setShowErrors(false);
      const employeeToSave = {
        ...selectedEmployee,
        ...form,
        branches: assignedBranches,
        phone: formatPhoneNumberField(form.phone).replace(/\D/g, ''),
      };
      saveEmployeeProfile(employeeToSave);
      changeScreen(Screens.LOGIN);
    }
  };

  const renderEmployeeConflicts = () => {
    const conflicts = employeeConflicts.map((employeeConflict) => {
      const employeeClicked = () => {
        onEmployeeSelected(employeeConflict);
        changeScreen(Screens.LOGIN);
      };

      return (
        <EmployeeResult
          key={`${employeeConflict.id}-${employeeConflict.conflictType}`}
          employee={employeeConflict}
          onClick={employeeClicked}
          className="employee-conflict"
        />
      );
    });

    return (
      <div className={`${cssBEM}__conflicts`}>
        <label>
          <i className="fa fa-exclamation-triangle" />
          <NeuroMessage id="employeeLogin.conflicts" tagName="span" />
        </label>
        <div className="employee-conflicts">{conflicts}</div>
      </div>
    );
  };

  const branchResultsRender = branchSearchResults.map((branch) => {
    const addBranch = () => {
      const branchesContain = assignedBranches.find((b) => b.id === branch.id);
      if (!branchesContain) {
        setAssignedBranches([...assignedBranches, branch]);
      }
    };
    return (
      <BranchResult key={branch.id} onClick={addBranch} branch={branch}>
        <FontAwesome name="plus-circle" />
      </BranchResult>
    );
  });

  const assignedBranchesRender = assignedBranches.map((branch) => {
    const removeBranch = () => {
      const updatedBranches = assignedBranches.filter((b) => {
        return branch.id !== b.id;
      });
      setAssignedBranches(updatedBranches);
    };
    return (
      <BranchResult key={branch.id} onClick={removeBranch} branch={branch}>
        <FontAwesome name="minus-circle" />
      </BranchResult>
    );
  });

  const formFieldsRender = (finalSchema.fields || []).map((field) => {
    const { name, type, validations, disabled = false } = field;
    const employee = selectedEmployee || {};
    return (
      <FormInput
        key={name}
        name={name}
        disabled={disabled}
        initialValue={employee[name] || ''}
        type={type}
        showErrors={showErrors}
        validations={validations}
        id="employeeLogin"
        validationError={messageContext.formatIntlMessage({
          id: `employeeLogin.formInput.${name}.error`,
        })}
        required={finalSchema.required.includes(name)}
      />
    );
  });

  return (
    <section className={cssBEM}>
      <FormContainer onSubmit={formSubmitted} onChange={formChanged}>
        <div className={`${cssBEM}__fields`}>{formFieldsRender}</div>

        <div className={`${cssBEM}__branch-search`}>
          <NeuroMessage id="employeeLogin.branches.search.label" tagName="label"/>
          <input
            type="text"
            name="branchSearch"
            placeholder={messageContext.formatIntlMessage({
              id: 'employeeLogin.branches.search.placeholder',
            })}
            onChange={searchValueChanged}
          />
          <div className="branch-search-results">
            {branchResultsRender.length ? (
              branchResultsRender
            ) : (
              <NeuroMessage
                id={
                  searchValue && initiatedSearch && !processing
                    ? 'employeeLogin.branches.search.empty'
                    : 'employeeLogin.branches.search.help'
                }
                tagName="p"
              />
            )}
          </div>
        </div>

        <div className={`${cssBEM}__branch-assignments`}>
          <NeuroMessage id="employeeLogin.branches.assigned" tagName="label" />
          <div className="branch-assignments">
            {assignedBranchesRender.length ? (
              assignedBranchesRender
            ) : (
              <NeuroMessage id="employeeLogin.branches.assigned.help" tagName="label"/>
            )}
          </div>
          {showErrors && !assignedBranches.length ? (
            <div className="form-input__error">
              <NeuroMessage id="employeeLogin.branches.assigned.error" />
            </div>
          ) : null}
        </div>

        {employeeConflicts.length ? renderEmployeeConflicts() : null}

        <div className="screen-actions">
          <button
            onClick={onCancel}
            className="screen-actions__btn screen-actions__btn--float-left screen-actions__btn--cancel"
          >
            <NeuroMessage id="employeeLogin.cancel" />
          </button>
          <button
            type="submit"
            className="screen-actions__btn screen-actions__btn--float-right"
            disabled={processing}
          >
            <NeuroMessage id="employeeLogin.save" />
          </button>
        </div>
      </FormContainer>
    </section>
  );
};

EditForm.propTypes = {
  ...LoginFlowScreenPropTypes,
  branches: PropTypes.arrayOf(BranchShape),
  employeeConflicts: PropTypes.array,
  onEmployeeSelected: PropTypes.func.isRequired,
  loadBranches: PropTypes.func.isRequired,
  saveEmployeeProfile: PropTypes.func.isRequired,
  searchEmployeeConflicts: PropTypes.func.isRequired,
  selectedEmployee: EmployeeShape,
  onCancel: PropTypes.func.isRequired,
};

export default EditForm;
